// Irreversible-delete confirmation: fetches what the delete would wipe (grades, sessions,
// attendance…) and requires typing SUPPRIMER whenever anything is affected.
import React, { useEffect, useState } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import CuteConfirmModal from '@/shared/components/CuteConfirmModal';
import { getDeleteImpact } from '@/services/filieresService';
import type { DeleteImpact, DeleteImpactTarget } from '@/types/filiere.types';

const CONFIRM_WORD = 'SUPPRIMER';

const EMPTY_IMPACT: DeleteImpact = {
  elements: 0,
  grades: 0,
  finalizations: 0,
  rattrapages: 0,
  scheduleEntries: 0,
  attendance: 0,
  groupHours: 0,
  groupsAffected: 0,
  studentsAffected: 0,
};

interface DeleteImpactModalProps {
  isOpen: boolean;
  /** One or more structures about to be deleted (impacts are summed) */
  targets: DeleteImpactTarget[];
  title: string;
  itemName?: string;
  message?: string;
  confirmLabel?: string;
  /** True while the confirmed delete is running */
  isLoading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const sumImpacts = (impacts: DeleteImpact[]): DeleteImpact =>
  impacts.reduce<DeleteImpact>(
    (acc, i) => ({
      elements: acc.elements + i.elements,
      grades: acc.grades + i.grades,
      finalizations: acc.finalizations + i.finalizations,
      rattrapages: acc.rattrapages + i.rattrapages,
      scheduleEntries: acc.scheduleEntries + i.scheduleEntries,
      attendance: acc.attendance + i.attendance,
      groupHours: acc.groupHours + i.groupHours,
      groupsAffected: acc.groupsAffected + i.groupsAffected,
      studentsAffected: acc.studentsAffected + i.studentsAffected,
    }),
    EMPTY_IMPACT,
  );

const plural = (count: number, singular: string, pluralForm: string): string =>
  `${count} ${count > 1 ? pluralForm : singular}`;

const hasLinkedData = (impact: DeleteImpact): boolean =>
  impact.grades > 0 ||
  impact.finalizations > 0 ||
  impact.rattrapages > 0 ||
  impact.scheduleEntries > 0 ||
  impact.attendance > 0 ||
  impact.groupHours > 0 ||
  impact.groupsAffected > 0 ||
  impact.studentsAffected > 0;

const DeleteImpactModal: React.FC<DeleteImpactModalProps> = ({
  isOpen,
  targets,
  title,
  itemName,
  message = 'Vérifiez ci-dessous les données qui seront supprimées avec cette structure.',
  confirmLabel = 'Supprimer définitivement',
  isLoading = false,
  onConfirm,
  onCancel,
}) => {
  const [impact, setImpact] = useState<DeleteImpact | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const targetsKey = targets.map((t) => `${t.type}:${t.id}`).join('|');

  useEffect(() => {
    if (!isOpen || targets.length === 0) {
      setImpact(null);
      setFetchError(null);
      return;
    }
    let cancelled = false;
    setIsFetching(true);
    setFetchError(null);
    Promise.all(targets.map((t) => getDeleteImpact(t)))
      .then((results) => {
        if (!cancelled) setImpact(sumImpacts(results));
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setImpact(null);
          setFetchError(err instanceof Error ? err.message : "Impossible d'évaluer l'impact de la suppression");
        }
      })
      .finally(() => {
        if (!cancelled) setIsFetching(false);
      });
    return () => {
      cancelled = true;
    };
    // targetsKey captures the identity of the targets array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, targetsKey]);

  const showElementCount = targets.some((t) => t.type !== 'element');
  const requiresTyping = fetchError !== null || (impact !== null && hasLinkedData(impact));

  const details = (
    <div className="space-y-3">
      {isFetching ? (
        <div className="flex items-center justify-center gap-2 py-3 text-sm text-gray-500">
          <Loader2 size={16} className="animate-spin" />
          Analyse des données liées…
        </div>
      ) : fetchError ? (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
          {fetchError}. Par prudence, la confirmation par saisie est exigée.
        </div>
      ) : impact ? (
        hasLinkedData(impact) || (showElementCount && impact.elements > 0) ? (
          <ul className="p-3 bg-red-50 border border-red-100 rounded-xl text-sm text-gray-800 space-y-1">
            {showElementCount && impact.elements > 0 && (
              <li>• {plural(impact.elements, 'élément de module', 'éléments de module')}</li>
            )}
            {impact.grades > 0 && <li>• {plural(impact.grades, "note d'étudiant", "notes d'étudiants")}</li>}
            {impact.finalizations > 0 && <li>• {plural(impact.finalizations, 'finalisation', 'finalisations')}</li>}
            {impact.rattrapages > 0 && <li>• {plural(impact.rattrapages, 'rattrapage', 'rattrapages')}</li>}
            {impact.scheduleEntries > 0 && (
              <li>• {plural(impact.scheduleEntries, "séance d'emploi du temps", "séances d'emploi du temps")}</li>
            )}
            {impact.attendance > 0 && <li>• {plural(impact.attendance, 'présence', 'présences')}</li>}
            {impact.groupHours > 0 && (
              <li>• {plural(impact.groupHours, 'volume horaire de groupe', 'volumes horaires de groupe')}</li>
            )}
            {(impact.groupsAffected > 0 || impact.studentsAffected > 0) && (
              <li>
                • {plural(impact.groupsAffected, 'groupe concerné', 'groupes concernés')},{' '}
                {plural(impact.studentsAffected, 'étudiant', 'étudiants')}
              </li>
            )}
          </ul>
        ) : (
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-600">
            Aucune note, séance ou présence n'est rattachée à cette structure.
          </div>
        )
      ) : null}

      <p className="flex items-start gap-2 text-sm font-semibold text-red-600">
        <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
        Cette action est irréversible. Ces données seront définitivement supprimées.
      </p>
    </div>
  );

  return (
    <CuteConfirmModal
      isOpen={isOpen}
      title={title}
      itemName={itemName}
      message={message}
      details={details}
      confirmWord={requiresTyping ? CONFIRM_WORD : undefined}
      confirmLabel={confirmLabel}
      cancelLabel="Annuler"
      variant="danger"
      size="md"
      isLoading={isLoading || isFetching}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
};

export default DeleteImpactModal;
